import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { Redirect, useHistory } from 'react-router'

import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState
} from 'recoil'

import {
  Button,
  BUTTON_SIZE_LG,
  BUTTON_VARIANT_PRIMARY,
  EmptyState,
  Flex,
  FlexItem,
  Loading,
  MessageNoResult,
  SPACING_40,
  SpacingVertical,
  ToastContainer,
  Tooltip
} from 'mailup-web-components'
import emptyStateThumb from 'mailup-web-components/resources/mailup-web-app/img/svg/empty-state-ab_test.svg'

import { isOfferLight } from 'Helpers/options'

import ErrorFeedback from 'Components/ErrorFeedback'
import PageContainer from 'Components/PageContainer'
import ModalRecover from 'Pages/AbTestList/components/ModalRecover/ModalRecover'
import Header from './components/Header'
import ModalDelete from './components/ModalDelete/ModalDelete'
import Pagination from './components/Pagination'
import Results from './components/Results'
import Search from './components/Search'

import { pageState } from 'Pages/AbTestList/recoil/state/page'
import './AbTestList.scss'
import {
  deleteABTestCallback,
  recoverABTestCallback,
  searchTestsCallback
} from './recoil/callbacks/callbacks'
import {
  modalDeleteState,
  modalRecoverState,
  resultsState,
  resultsStateEmptyState
} from './recoil/state/results'
import { paginationState } from './recoil/state/search'

const AbTestList = () => {
  const intl = useIntl()
  const history = useHistory()
  const isEmptyState = useRecoilValue(resultsStateEmptyState)
  const { loading, error, loaded, fetchingResults, severity } = useRecoilValue(
    pageState
  )
  const { show, state, name, idABTest } = useRecoilValue(modalDeleteState)
  const { show: recoverShow, idABTest: recoverIdABTest } = useRecoilValue(
    modalRecoverState
  )
  const handelDeleteCancel = useResetRecoilState(modalDeleteState)
  const handelRecoverCancel = useResetRecoilState(modalRecoverState)

  const { items, totalItems } = useRecoilValue(resultsState)
  const [{ pageNum, pageSize }, setPaginationState] = useRecoilState(
    paginationState
  )
  const fetchResults = useRecoilCallback(searchTestsCallback, [])
  const deleteABTest = useRecoilCallback(deleteABTestCallback, [])
  const recoverABTest = useRecoilCallback(recoverABTestCallback, [])

  useEffect(() => {
    !loaded && fetchResults(true)
  }, [loaded])

  useEffect(() => {
    loaded && fetchResults()
  }, [pageNum, pageSize])

  const handelDeleteConfirm = () => {
    deleteABTest(idABTest).then(e => {
      if (e) {
        if (pageNum > 1) {
          const r = totalItems % pageSize
          if (r === 1) {
            setPaginationState(prev => ({
              ...prev,
              pageNum: pageNum - 1
            }))
          }
        } else {
          fetchResults()
        }
      }
    })
  }

  const handelRecoverConfirm = () => {
    recoverABTest(idABTest)
  }

  const newAbTest = () => {
    history.push('/messages/email/abtest/0')
  }

  return (
    <>
      <Header />
      <Loading open={loading || fetchingResults} />
      <Tooltip />
      <ToastContainer />
      <ModalDelete
        stato={state}
        name={name}
        show={show}
        idABTest={idABTest}
        handleCancel={handelDeleteCancel}
        handleConfirm={handelDeleteConfirm}
        appendToDataMuTest='ab-test-delete-item'
      />
      <ModalRecover
        show={recoverShow}
        idABTest={recoverIdABTest}
        handleCancel={handelRecoverCancel}
        handleConfirm={handelRecoverConfirm}
        appendToDataMuTest='ab-test-recover-item'
      />
      <PageContainer>
        <ErrorFeedback
          error={error}
          severity={severity}
          onClickReload={fetchResults}
        />
        {!error && (
          <>
            {!loading && isEmptyState && (
              <EmptyState
                thumbSrc={emptyStateThumb}
                title={intl.formatMessage({
                  id: 'Load.AbTest.EmptyStateTitle'
                })}
                description={intl.formatMessage({
                  id: 'Load.AbTest.EmptyStateDescription'
                })}
                buttonText={intl.formatMessage({
                  id: 'Load.AbTest.NewButton'
                })}
                onClickButton={newAbTest}
              />
            )}
            {!loading && !isEmptyState && (
              <>
                <Search />
                <SpacingVertical variant={SPACING_40} />
                <>
                  {items.length === 0 && (
                    <MessageNoResult
                      text={intl.formatMessage({
                        id: 'Load.SearchRecipients.noResultsTitle'
                      })}
                    />
                  )}
                  {items.length > 0 && (
                    <>
                      <Results />
                      <Pagination />
                    </>
                  )}

                  <Flex
                    flexDirection='column'
                    justifyContent='flex-end'
                    margin='20px 0 0 0'
                  >
                    <FlexItem>
                      <Button
                        kind={BUTTON_VARIANT_PRIMARY}
                        size={BUTTON_SIZE_LG}
                        data-mu-test='cta-NewTask'
                        onClick={newAbTest}
                      >
                        {intl.formatMessage({ id: 'Load.AbTest.NewButton' })}
                      </Button>
                    </FlexItem>
                  </Flex>
                </>
              </>
            )}
          </>
        )}
      </PageContainer>
    </>
  )
}

const withGuard = Component => {
  const WithGuardComponent = props => {
    if (isOfferLight()) {
      return <Redirect to='/messages/email/abtest-upsell' />
    }

    return <Component {...props} />
  }

  return WithGuardComponent
}

export default withGuard(AbTestList)
